import { Mail } from 'lucide-react';
import { useRef, useState, type JSX } from 'react';
import { toast } from 'sonner';

// Import our wizard steps
import { LanguageSelector } from '@/components/LanguageSelector/LanguageSelector';
import { PdfPreview } from '@/components/PdfPreview/PdfPreview';
import CompanyInfoPage from '@/components/registration/CompanyInfoPage/CompanyInfoPage';
import CompanySignaturePage from '@/components/registration/CompanySignaturePage/CompanySignaturePage';
import ContactPersonInfoPage from '@/components/registration/ContactPersonInfoPage/ContactPersonInfoPage';
import FinancialYearPage from '@/components/registration/FinancialYearPage/FinancialYearPage';
import InformationPage from '@/components/registration/InformationPage/InformationPage';
import OtherInfoPage from '@/components/registration/OtherInfoPage/OtherInfoPage';
import RepresentativesPage from '@/components/registration/RepresentativesPage/RepresentativesPage';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/lib/contexts/LanguageContext';
import { PdfService } from '@/lib/services/pdfService';

// Define form data type
interface FormData {
  // Contact Person
  countryOfResidence: string;
  personalId: string;
  otherCitizenship: boolean;
  citizenshipCountries: string;
  firstName: string;
  lastName: string;
  companyName: string;
  careOf: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  phone: string;
  mobile: string;
  email: string;

  // Company Information
  registrationNumber: string;
  foundingDate: string;
  companyType: string;
  taxNumber: string;

  // Financial Year
  financialYearStart: string;
  financialYearEnd: string;
  financialYearType: string;
  customFinancialYearPeriod: string;

  // Representatives
  boardMembers: string[];
  ceo: string;

  // Share Capital
  shareCapitalAmount: string;
  higherShareCapital: boolean;
  shareSplit: boolean;

  // Representatives Table
  representativesTable: Array<{
    id: string;
    role: string;
    name: string;
    address: string;
  }>;

  // Company Signature
  signatureType: string;
  signatureDetails: string;
  signaturePersons: string[];

  // Bank and Authorizations
  bankName: string;
  accountNumber: string;
  authorizedPersons: string[];

  // Reservations
  hasReservations: boolean;
  reservationDetails: string;

  // Auditor
  hasAuditor: boolean;
  auditorName: string;
  auditorContact: string;

  // Other Information
  additionalInfo: string;
  deliveryEmail: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  dataComplete: boolean;
  submitNow: boolean;
  digitalSignature: string;
  submitConfirmation: boolean;

  // Allow string indexing
  [key: string]: any;
}

const OPTIONAL_STEP_FIELDS = new Set(['mobile', 'careOf', 'citizenshipCountries']);

function getStepCircleClassName(index: number, currentStep: number): string {
  if (index < currentStep) {
    return 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400';
  }

  if (currentStep === index) {
    return 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400';
  }

  return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
}

function isInvalidFieldValue(
  field: string,
  value: unknown,
  representativesTable: Array<{ name: string; role: string }> | undefined,
): boolean {
  if (OPTIONAL_STEP_FIELDS.has(field)) {
    return false;
  }

  if (field === 'representativesTable') {
    const table = Array.isArray(representativesTable) ? representativesTable : [];
    return !table.some((row) => row?.name?.trim() && row?.role?.trim());
  }

  if (typeof value === 'string') {
    return !value.trim();
  }

  if (typeof value === 'boolean') {
    return !value;
  }

  return Array.isArray(value) && value.length === 0;
}

function generateDefaultValues(): FormData {
  return {
    // Contact Person
    countryOfResidence: 'Sweden',
    personalId: '19900101-1234',
    otherCitizenship: false,
    citizenshipCountries: '',
    firstName: 'Test',
    lastName: 'Testsson',
    companyName: 'Test Company AB',
    careOf: '',
    streetAddress: 'Testgatan 123',
    postalCode: '12345',
    city: 'Stockholm',
    phone: '+46-8-123-4567',
    mobile: '+46-70-123-4567',
    email: 'test@example.com',

    // Company Information
    registrationNumber: '556123-4567',
    foundingDate: '2024-01-01',
    companyType: 'Aktiebolag',
    taxNumber: 'SE556123456701',

    // Financial Year
    financialYearStart: '2024-01-01',
    financialYearEnd: '2024-12-31',
    financialYearType: 'calendar',
    customFinancialYearPeriod: '',

    // Representatives
    boardMembers: ['Test Testsson', 'Jane Doe'],
    ceo: 'Test Testsson',

    // Share Capital
    shareCapitalAmount: '25000',
    higherShareCapital: false,
    shareSplit: false,

    // Representatives Table
    representativesTable: [
      { id: '1', role: 'Aktieägare', name: 'Test Testsson', address: 'Testgatan 123, Stockholm' },
      {
        id: '2',
        role: 'Styrelseledamot',
        name: 'Jane Doe',
        address: 'Företagsgatan 456, Göteborg',
      },
      { id: '3', role: 'VD', name: 'Test Testsson', address: 'Testgatan 123, Stockholm' },
    ],

    // Company Signature
    signatureType: 'Firman tecknas av styrelsen',
    signatureDetails: 'Kräver styrelsens godkännande',
    signaturePersons: ['Test Testsson', 'Jane Doe'],

    // Bank and Authorizations
    bankName: 'Swedbank',
    accountNumber: '1234-56 789 12',
    authorizedPersons: ['Test Testsson'],

    // Reservations
    hasReservations: false,
    reservationDetails: '',

    // Auditor
    hasAuditor: false,
    auditorName: '',
    auditorContact: '',

    // Other Information
    additionalInfo: 'Test information',
    deliveryEmail: 'test@example.com',
    termsAccepted: true,
    privacyAccepted: true,
    dataComplete: true,
    submitNow: false,
    digitalSignature: 'Test Testsson',
    submitConfirmation: false,
  };
}

export default function RegistrationWizard(): JSX.Element {
  const { t } = useLanguage();
  const formRefs = useRef<Array<HTMLFormElement | null>>([
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ]);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    // Contact Person
    countryOfResidence: '',
    personalId: '',
    otherCitizenship: false,
    citizenshipCountries: '',
    firstName: '',
    lastName: '',
    companyName: '',
    careOf: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    phone: '',
    mobile: '',
    email: '',

    // Company Information
    registrationNumber: '',
    foundingDate: '',
    companyType: '',
    taxNumber: '',

    // Financial Year
    financialYearStart: '',
    financialYearEnd: '',
    financialYearType: '',
    customFinancialYearPeriod: '',

    // Representatives
    boardMembers: [],
    ceo: '',

    // Share Capital
    shareCapitalAmount: '',
    higherShareCapital: false,
    shareSplit: false,

    // Representatives Table
    representativesTable: [
      { id: '1', role: 'Aktieägare', name: '', address: '' },
      { id: '2', role: 'Styrelseledamot', name: '', address: '' },
      { id: '3', role: 'Styrelsesuppleant', name: '', address: '' },
    ],

    // Company Signature
    signatureType: '',
    signatureDetails: '',
    signaturePersons: [],

    // Bank and Authorizations
    bankName: '',
    accountNumber: '',
    authorizedPersons: [],

    // Reservations
    hasReservations: false,
    reservationDetails: '',

    // Auditor
    hasAuditor: false,
    auditorName: '',
    auditorContact: '',

    // Other Information
    additionalInfo: '',
    deliveryEmail: '',
    termsAccepted: false,
    privacyAccepted: false,
    dataComplete: false,
    submitNow: false,
    digitalSignature: '',
    submitConfirmation: false,
  });

  // Add state for PDF preview
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Add state for fill defaults checkbox
  const [fillDefaults, setFillDefaults] = useState(false);

  // Handle fill defaults checkbox change
  const handleFillDefaultsChange = (checked: boolean): void => {
    setFillDefaults(checked);
    if (checked) {
      const defaultValues = generateDefaultValues();
      setFormData(defaultValues);
      toast.success(t.fillDefaultsSuccess);
    }
  };

  // Group fields by step for validation
  const stepFields = [
    // Information (no validation required)
    [],
    // Contact Person
    ['firstName', 'lastName', 'streetAddress', 'postalCode', 'city', 'phone', 'email'],
    // Company Information
    ['companyName', 'registrationNumber'],
    // Financial Year
    ['financialYearType'],
    // Representatives
    ['shareCapitalAmount', 'representativesTable'],
    // Company Signature
    ['signatureType'],
    // Other Information
    ['deliveryEmail', 'termsAccepted', 'privacyAccepted', 'dataComplete', 'submitConfirmation'],
  ];

  const steps = [
    { id: 'information', name: t.information, component: InformationPage },
    { id: 'contactPerson', name: t.companyInfo, component: ContactPersonInfoPage },
    { id: 'companyInfo', name: t.companyInformation, component: CompanyInfoPage },
    { id: 'financialYear', name: t.contact, component: FinancialYearPage },
    { id: 'representatives', name: t.businessDetails, component: RepresentativesPage },
    { id: 'companySignature', name: t.confirmation, component: CompanySignaturePage },
    { id: 'otherInfo', name: t.otherInfo, component: OtherInfoPage },
  ];

  const handleInputChange = (_section: string, field: string, value: any): void => {
    console.warn(`Setting ${field} to:`, value);

    // Update the form data with the new value using the field name
    // This is a flat structure, so we don't need to use the section
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateCurrentStep = (): boolean => {
    const fieldsToValidate = stepFields[currentStep] || [];
    const hasInvalidRequiredField = fieldsToValidate.some((field) =>
      isInvalidFieldValue(field, formData[field], formData.representativesTable),
    );

    const formElement = formRefs.current[currentStep];
    if (formElement && !formElement.checkValidity()) {
      formElement.reportValidity();
      return false;
    }

    if (hasInvalidRequiredField) {
      toast.error(t.fieldRequired);
      return false;
    }

    return true;
  };

  const goToNextStep = (): void => {
    // Validate current step before proceeding
    const valid = validateCurrentStep();
    if (!valid) {
      // Additional guard toast (validateCurrentStep already emits a specific toast)
      toast.error(t.pleaseCompleteAllFields);
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousStep = (): void => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // New function to navigate directly to a specific step without validation
  const navigateToStep = (stepIndex: number): void => {
    setCurrentStep(stepIndex);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (): Promise<void> => {
    // First check if the last step's fields are valid
    if (!validateCurrentStep()) {
      toast.error(t.pleaseCompleteAllFields);
      return;
    }

    // Specifically check for the submitConfirmation field
    if (!formData.submitConfirmation) {
      toast.error(t.pleaseConfirmSubmission);
      return;
    }

    // Show a loading toast
    const loadingToast = toast.loading(t.downloadingTemplate);

    try {
      // Generate the PDF from form data
      const generatedPdfBytes = await PdfService.generateRegistrationPdf(formData);

      // Dismiss the loading toast
      toast.dismiss(loadingToast);

      // Set the PDF bytes and show the preview
      setPdfBytes(generatedPdfBytes);
      setShowPreview(true);

      console.warn('Form submitted:', formData);
    } catch (error) {
      // Dismiss the loading toast
      toast.dismiss(loadingToast);

      // Show error message
      toast.error(t.templateProcessingError);
      console.error('Error generating PDF:', error);
    }
  };

  const CurrentStepComponent =
    steps[currentStep]?.component || steps[0]?.component || ContactPersonInfoPage;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Check if the form is valid for enabling/disabling the Submit button
  const isLastStepValid =
    currentStep === steps.length - 1 ? validateCurrentStep() && formData.submitConfirmation : true;

  // Debug logging for validation
  if (currentStep === steps.length - 1) {
    console.warn('Last step validation:', {
      isValid: validateCurrentStep(),
      submitConfirmed: formData.submitConfirmation,
      isLastStepValid,
    });
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Left sidebar navigation */}
      <div className="hidden h-full w-64 flex-col border-r border-gray-200 bg-white p-6 md:block dark:border-gray-800 dark:bg-gray-900">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold dark:text-white">{t.companyRegistration}</h2>
        </div>
        <div className="mb-6 flex items-center gap-2">
          <LanguageSelector />
        </div>
        <Separator className="mb-4" />
        <nav>
          {steps.map((step, index) => (
            <div key={step.id} className="mb-4">
              <button
                onClick={() => navigateToStep(index)}
                className={`flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  currentStep === index
                    ? 'bg-gray-100 font-medium text-black dark:bg-gray-800 dark:text-white'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                }`}
              >
                <div
                  className={`mr-3 flex h-6 w-6 items-center justify-center rounded-full ${getStepCircleClassName(index, currentStep)}`}
                >
                  {index + 1}
                </div>
                {step.name}
              </button>
            </div>
          ))}
        </nav>

        {/* Contact section at bottom */}
        <div className="mt-auto pt-96">
          <Separator className="mb-4" />
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <h3 className="mb-2 font-medium">{t.contactSection}</h3>
            <a
              href="mailto:victor@cerneaconsulting.com"
              className="flex items-center gap-2 text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Mail className="h-4 w-4" />
              victor@cerneaconsulting.com
            </a>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 p-6 lg:p-8">
        <div className="mx-auto max-w-3xl">
          {/* Mobile header with progress */}
          <div className="mb-6 block md:hidden">
            <div className="mb-2 flex items-center justify-between">
              <h1 className="text-2xl font-bold dark:text-white">{t.companyRegistration}</h1>
              <div className="flex items-center gap-2">
                <LanguageSelector />
              </div>
            </div>
            <div className="mb-2 flex items-center space-x-1">
              {steps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => navigateToStep(index)}
                  className={`flex h-6 w-6 items-center justify-center rounded-full ${getStepCircleClassName(index, currentStep)}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              {t.step} {currentStep + 1} {t.of} {steps.length}
            </p>
            <Progress value={progress} className="mb-4 h-2" />
          </div>

          {/* Desktop progress indicator */}
          <div className="mb-8 hidden md:block">
            <div className="mb-2 flex items-start justify-between">
              <h1 className="text-2xl font-bold dark:text-white">
                {steps[currentStep]?.name || t.companyRegistration}
              </h1>
              <div className="ml-4 flex items-center space-x-2">
                <Checkbox
                  id="fill-defaults"
                  checked={fillDefaults}
                  onCheckedChange={handleFillDefaultsChange}
                  className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                />
                <label
                  htmlFor="fill-defaults"
                  className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300"
                  title={t.fillDefaultsHelp}
                >
                  {t.fillDefaults}
                </label>
              </div>
            </div>
            <div className="mb-2 flex items-center space-x-1">
              {steps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => navigateToStep(index)}
                  className={`flex h-8 w-8 items-center justify-center rounded-full ${getStepCircleClassName(index, currentStep)}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              {t.step} {currentStep + 1} {t.of} {steps.length}
            </p>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Form card */}
          <Card className="w-full">
            <CardContent className="pt-6">
              <form
                ref={(el) => {
                  formRefs.current[currentStep] = el;
                }}
                onSubmit={(e) => {
                  e.preventDefault(); // Always prevent default form submission
                }}
                noValidate
              >
                <CurrentStepComponent
                  formData={formData}
                  onChange={handleInputChange}
                  onInputChange={function (): void {
                    throw new Error('Function not implemented.');
                  }}
                />

                <div className="mt-8 flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={goToPreviousStep}
                    disabled={currentStep === 0}
                  >
                    {t.previous}
                  </Button>

                  {currentStep === steps.length - 1 ? (
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          console.warn('Resetting submit confirmation');
                          setFormData((prev) => ({
                            ...prev,
                            submitConfirmation: false,
                          }));
                          setTimeout(() => {
                            setFormData((prev) => ({
                              ...prev,
                              submitConfirmation: true,
                            }));
                          }, 100);
                        }}
                      >
                        {t.reset}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!isLastStepValid}
                        className={isLastStepValid ? '' : 'cursor-not-allowed opacity-50'}
                      >
                        {t.submitRegistration}
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" onClick={goToNextStep}>
                      {t.continue}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && (
        <PdfPreview pdfBytes={pdfBytes} onClose={() => setShowPreview(false)} formData={formData} />
      )}
    </div>
  );
}
